import React, { useState } from 'react';
import type { FC } from 'react';
import { Spinner, useAsync } from '@wix/cli-ui-kit';
import type { Site } from '@wix/site-list-client';
import { match } from 'variant';
import { useTranslation } from '@wix/cli-i18n';
import { useDevCenterClient } from '@wix/dev-center-client';
import { WaitForAppToBeInstalled } from './WaitForAppToBeInstalled.js';
import { SiteListSelect } from './SiteListSelect.js';

const CheckAppInstalledOnSite: FC<{
  site: Site;
  appId: string;
  onSubmit: (site: Site) => void;
  onError: (error: unknown) => void;
}> = ({ appId, site, onSubmit, onError }) => {
  const { t } = useTranslation();
  const { isAppInstalled } = useDevCenterClient({ siteId: site.id });

  const { status } = useAsync(async () => isAppInstalled(appId), [], {
    onError,
    onSuccess: (isInstalled) => {
      if (isInstalled) {
        onSubmit(site);
      }
    },
  });

  return match(status, {
    Error: () => null,
    Loading: () => <Spinner text={t('general.loading')} />,
    Success: ({ result: isInstalled }) => {
      if (isInstalled) {
        return null;
      }

      return (
        <WaitForAppToBeInstalled
          appId={appId}
          site={site}
          onSubmit={() => onSubmit(site)}
          onError={onError}
        />
      );
    },
  });
};

interface Props {
  sites: Site[];
  appId: string;
  onSubmit: (site: Site) => void;
  onError: (error: unknown) => void;
}

export const SelectExistingSite = ({
  sites,
  appId,
  onSubmit,
  onError,
}: Props) => {
  const [siteToInstallAppOn, setSiteToInstallAppOn] = useState<Site | null>(
    null
  );

  return (
    <>
      <SiteListSelect
        onSubmit={(site) => setSiteToInstallAppOn(site)}
        sites={sites}
      />
      {siteToInstallAppOn && (
        <CheckAppInstalledOnSite
          appId={appId}
          site={siteToInstallAppOn}
          onSubmit={() => onSubmit(siteToInstallAppOn)}
          onError={onError}
        />
      )}
    </>
  );
};
